import type { ReactNode } from 'react';
import { RadioGroupItem } from '@parrot-software/design-system';
import { cn } from '@parrot-software/utils';

type PaymentMethodCardProps = {
  children: ReactNode;
  disabled?: boolean;
  icon: ReactNode;
  isSelected: boolean;
  title: string;
  value: string;
};

/**
 * Generic payment-method card used inside a `RadioGroup`.
 *
 * Wraps a `<RadioGroupItem>` in a label with icon, title and an arbitrary
 * `children` body. Selection state is driven from the parent `RadioGroup`
 * value — this component is presentation-only. When `disabled` is set, the card
 * is dimmed, not clickable, and its radio item cannot be selected (used for the
 * SPEI card once the block is already configured with SPEI).
 *
 * @param props - Component properties
 * @returns React element rendering the card
 */
export const PaymentMethodCard = ({
  children,
  disabled = false,
  icon,
  isSelected,
  title,
  value,
}: PaymentMethodCardProps) => {
  const inputId = `payment-method-${value}`;
  return (
    <label
      className={cn('flex w-full items-start gap-3 rounded-xl border bg-popover p-6 shadow-xs transition-colors', {
        'cursor-not-allowed border-border opacity-60': disabled,
        'cursor-pointer': !disabled,
        'border-2 border-primary': !disabled && isSelected,
        'border-border hover:border-gray-300': !disabled && !isSelected,
      })}
      htmlFor={inputId}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex items-center gap-2">
          {icon}
          <p className="text-lg font-semibold leading-7 text-foreground">{title}</p>
        </div>
        {children}
      </div>
      <RadioGroupItem className="mt-1 h-4 w-4 shrink-0" disabled={disabled} id={inputId} value={value} />
    </label>
  );
};
